import { ApprovalPolicyContext } from '@felix/ui';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApiError, type Me, officeApi, type SessionStage } from './api.js';

interface SessionValue {
  /** `undefined`: henüz bilinmiyor; `null`: oturum yok. */
  me: Me | null | undefined;
  stage: SessionStage | null | undefined;
  refresh: () => Promise<SessionStage | null>;
  setStage: (stage: SessionStage | null) => void;
  /**
   * İlk girişte yazılan geçici parola, yalnız bellekte ve yalnız parola değiştirilene kadar:
   * kişi aynı parolayı ikinci kez yazmasın diye. Sayfa yenilenince kaybolur, alan yeniden sorulur.
   */
  pendingPassword: string | null;
  setPendingPassword: (password: string | null) => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [pendingPassword, setPendingPassword] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const current = await officeApi.me();
      setMe(current);
      return current.stage;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setMe(null);
        return null;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => setMe(null));
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      stage: me === undefined ? undefined : (me?.stage ?? null),
      refresh,
      setStage: (stage) =>
        setMe((current) => (stage === null ? null : { stage, user: current?.user ?? { id: '' } })),
      pendingPassword,
      setPendingPassword,
    }),
    [me, refresh, pendingPassword],
  );
  // Onay penceresinde parola alanı: şirket ayarı profilden (ADR 0056 §4)
  const reauthAlways = me?.approvalPolicy?.reauthAlways === true;
  const approvalPolicy = useMemo(() => ({ reauthAlways }), [reauthAlways]);
  return (
    <SessionContext.Provider value={value}>
      <ApprovalPolicyContext.Provider value={approvalPolicy}>
        {children}
      </ApprovalPolicyContext.Provider>
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('SessionProvider eksik');
  return value;
}
